import { useCallback, useState } from "react";
import { DEFAULT_CONNECTOR_FORM } from "../constants";
import type { AccessLevel, AuthHeader, ConnectorFormValues } from "../types";

type FieldSetter = <K extends keyof ConnectorFormValues>(
  field: K,
  value: ConnectorFormValues[K],
) => void;

type UseConnectorFormResult = {
  values: ConnectorFormValues;
  setField: FieldSetter;
  setAccessLevel: (level: AccessLevel) => void;
  setAuthHeader: (header: AuthHeader) => void;
  reset: (next?: ConnectorFormValues) => void;
  isValid: boolean;
};

const computeIsValid = (values: ConnectorFormValues): boolean => {
  if (!values.alias.trim() || !values.serverUrl.trim() || !values.transport) {
    return false;
  }
  if (values.accessLevel === "project" && !values.projectId) {
    return false;
  }
  if (values.accessLevel === "user" && values.users.length === 0) {
    return false;
  }
  return true;
};

export const useConnectorForm = (): UseConnectorFormResult => {
  const [values, setValues] = useState<ConnectorFormValues>(DEFAULT_CONNECTOR_FORM);

  const setField: FieldSetter = useCallback((field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  }, []);

  // Keep each tab's draft selection while the dialog is open. The payload
  // mapper submits only the active access level, so retaining inactive values
  // lets users compare or correct another tab without losing prior work.
  const setAccessLevel = useCallback((level: AccessLevel) => {
    setValues((prev) => ({
      ...prev,
      accessLevel: level,
    }));
  }, []);

  // Switching auth type clears the credentials for the previous type so we
  // don't submit a stale secret the user no longer intended.
  const setAuthHeader = useCallback((header: AuthHeader) => {
    setValues((prev) => ({
      ...prev,
      authHeader: header,
      oauthClientId: header === "oauth" ? prev.oauthClientId : "",
      oauthClientSecret: header === "oauth" ? prev.oauthClientSecret : "",
      apiKey: header === "api_key" ? prev.apiKey : "",
      bearerToken: header === "bearer" ? prev.bearerToken : "",
    }));
  }, []);

  const reset = useCallback(
    (next: ConnectorFormValues = DEFAULT_CONNECTOR_FORM) => setValues(next),
    [],
  );

  return {
    values,
    setField,
    setAccessLevel,
    setAuthHeader,
    reset,
    isValid: computeIsValid(values),
  };
};
